import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { TRANSACTION_TYPES, translateSubType } from '@/utils/transactionTypes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddTransactionForm = ({ employeeId, isRTL, onClose, onSuccess, editData }) => {
  const { t } = useTranslation();
  const isEdit = !!editData;

  const [form, setForm] = useState({
    main_type: '', sub_type: '', status: 'completed',
    work_date: new Date(),
    duration_days: 0, price: '', actual_paid_amount: '', notes: ''
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        main_type: editData.main_type || '',
        sub_type: editData.sub_type || '',
        status: editData.status || 'completed',
        work_date: editData.work_date ? new Date(editData.work_date) : new Date(),
        duration_days: editData.duration_days || 0,
        price: editData.price?.toString() || '',
        actual_paid_amount: editData.actual_paid_amount?.toString() || '',
        notes: editData.notes || ''
      });
    }
  }, [editData]);

  const handleSubmit = async () => {
    try {
      const workDateStr = format(form.work_date, 'yyyy-MM-dd');
      const payload = {
        main_type: form.main_type,
        sub_type: form.sub_type,
        status: form.status,
        work_date: workDateStr,
        price: parseFloat(form.price),
        actual_paid_amount: parseFloat(form.actual_paid_amount),
        duration_days: parseInt(form.duration_days),
        notes: form.notes || ''
      };
      if (isEdit) {
        await axios.put(`${API}/sub-transactions/${editData.id}`, payload);
      } else {
        await axios.post(`${API}/sub-transactions`, { ...payload, employee_id: employeeId });
      }
      toast.success(t('success'));
      onSuccess();
      onClose();
    } catch { toast.error(t('error')); }
  };

  const formatDateDMY = (date) => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  return (
    <div className={`p-3 md:p-4 border rounded-lg space-y-3 bg-slate-50 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t('mainType')}</Label>
          <Select value={form.main_type}
            onValueChange={(val) => setForm({ ...form, main_type: val, sub_type: '' })}>
            <SelectTrigger className="h-9 text-sm" dir={isRTL ? 'rtl' : 'ltr'}><SelectValue /></SelectTrigger>
            <SelectContent dir={isRTL ? 'rtl' : 'ltr'}>
              <SelectItem value="new_residence">{t('new_residence')}</SelectItem>
              <SelectItem value="renewal">{t('renewal')}</SelectItem>
              <SelectItem value="cancellation">{t('cancellation')}</SelectItem>
              <SelectItem value="temp_work_permit">{t('temp_work_permit')}</SelectItem>
              <SelectItem value="other">{t('other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t('subType')}</Label>
          {form.main_type === 'other' ? (
            <Input value={form.sub_type} className="h-9 text-sm"
              onChange={(e) => setForm({ ...form, sub_type: e.target.value })} />
          ) : (
            <Select value={form.sub_type}
              onValueChange={(val) => setForm({ ...form, sub_type: val })}>
              <SelectTrigger className="h-9 text-sm" dir={isRTL ? 'rtl' : 'ltr'}><SelectValue /></SelectTrigger>
              <SelectContent dir={isRTL ? 'rtl' : 'ltr'}>
                {TRANSACTION_TYPES[form.main_type]?.map((type) => (
                  <SelectItem key={type} value={type}>{translateSubType(type, isRTL)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div>
          <Label className="text-xs">{t('price')}</Label>
          <Input type="number" value={form.price} className="h-9 text-sm"
            onChange={(e) => setForm({ ...form, price: e.target.value })} dir="ltr" />
        </div>
        <div>
          <Label className="text-xs">{t('actualPaidAmount')}</Label>
          <Input type="number" value={form.actual_paid_amount} className="h-9 text-sm"
            onChange={(e) => setForm({ ...form, actual_paid_amount: e.target.value })} dir="ltr" />
        </div>
        <div>
          <Label className="text-xs">{t('workDate')}</Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="tx-work-date-picker"
                className="w-full h-9 text-sm justify-start font-normal mt-0.5"
                dir="ltr"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                {formatDateDMY(form.work_date)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={form.work_date}
                onSelect={(date) => {
                  if (date) {
                    setForm({ ...form, work_date: date });
                    setCalendarOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-xs">{t('status')}</Label>
          <Select value={form.status}
            onValueChange={(val) => setForm({ ...form, status: val })}>
            <SelectTrigger className="h-9 text-sm" dir={isRTL ? 'rtl' : 'ltr'}><SelectValue /></SelectTrigger>
            <SelectContent dir={isRTL ? 'rtl' : 'ltr'}>
              <SelectItem value="completed">{isRTL ? 'مكتملة' : 'Completed'}</SelectItem>
              <SelectItem value="pending">{isRTL ? 'قيد الإجراء' : 'In Progress'}</SelectItem>
              <SelectItem value="waiting_approval">{isRTL ? 'بانتظار موافقة السيستم' : 'Awaiting Approval'}</SelectItem>
              <SelectItem value="cancelled">{isRTL ? 'ملغية' : 'Cancelled'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs">{isRTL ? 'ملاحظات' : 'Notes'}</Label>
        <Input value={form.notes} className="h-9 text-sm" data-testid="tx-notes-input"
          placeholder={isRTL ? 'أضف ملاحظات...' : 'Add notes...'}
          onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
        <Button size="sm" className="h-8 text-xs" onClick={handleSubmit}
          style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}>
          {isEdit ? (isRTL ? 'تحديث' : 'Update') : t('save')}
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>{t('cancel')}</Button>
      </div>
    </div>
  );
};
